import { DATA, DATA_LABEL } from '../../theme/type.js';
import { observationGroups, signed, type MwTrackWire } from '../model/mw.js';

/**
 * The staged observation list (design 915–930) — **where the two nulls are drawn
 * apart**.
 *
 * | state | wire | this component |
 * | --- | --- | --- |
 * | stage has not fired | no observation for it | **no group and no row** |
 * | constellation dropped the point | `value: null` | a row, with `—` in it |
 *
 * Both are absences and they justify opposite decisions, which is why M4-D5 makes
 * them different shapes on the wire and why they have to stay different shapes
 * here. A seat that cannot tell "stage 3 has not happened" from "stage 3 happened
 * and I was not told the apogee" cannot decide whether waiting will help.
 *
 * The dash is `data-dropped` as well as visible, because the *only* thing that
 * distinguishes it from a value in a screenshot is one glyph, and a test that
 * asserted on the glyph alone would pass against a component that drew a dash for
 * both.
 */

export interface ObservationBlockProps {
  readonly track: MwTrackWire;
}

export function ObservationBlock(props: ObservationBlockProps): JSX.Element {
  const groups = observationGroups(props.track);

  if (groups.length === 0) {
    return (
      <div
        data-testid="mw-observations-empty"
        style={{
          ...DATA_LABEL,
          flex: 'none',
          padding: '18px 14px',
          borderBottom: '1px solid var(--border)',
          textAlign: 'center',
          color: 'var(--text-muted)',
        }}
      >
        NO DATA YET
      </div>
    );
  }

  return (
    <div
      data-testid="mw-observations"
      style={{
        flex: 'none',
        display: 'flex',
        flexDirection: 'column',
        padding: '6px 0 10px',
        borderBottom: '1px solid var(--border)',
      }}
    >
      {groups.map((group) => (
        <div key={group.stage} data-testid="mw-stage-group" data-stage={group.stage}>
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 8,
              padding: '6px 14px 3px',
            }}
          >
            <span style={{ ...DATA_LABEL, flex: 1, color: 'var(--text-2)' }}>
              STAGE {group.stage}
            </span>
            {/* The price of having waited this long, on the stage that set it. */}
            <span
              data-testid="mw-stage-credit"
              data-stage={group.stage}
              style={{
                ...DATA,
                flex: 'none',
                // A stage-4 zero is the number the whole wait-or-act mechanic
                // turns on, not an absence. `--text-disabled` is the tier for a
                // value that is genuinely not there, and it was making the
                // loudest fact on the board the quietest thing in its block.
                color: group.credit === 0 ? 'var(--text-dim)' : 'var(--text-muted)',
              }}
            >
              {signed(group.credit)}
            </span>
          </div>
          {group.rows.map((row) => (
            <div
              key={row.field}
              data-testid={'mw-obs-' + row.field}
              data-dropped={row.value === null ? 'true' : undefined}
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 12,
                padding: '4px 14px',
                borderBottom: '1px solid var(--rule-faint)',
              }}
            >
              <span
                style={{ ...DATA_LABEL, flex: 'none', fontWeight: 400, color: 'var(--text-muted)' }}
              >
                {row.label}
              </span>
              <span
                data-testid="mw-obs-value"
                style={{
                  ...DATA,
                  flex: 1,
                  minWidth: 0,
                  textAlign: 'right',
                  fontWeight: 500,
                  wordBreak: 'break-word',
                  color: row.value === null ? 'var(--warn)' : 'var(--text-2)',
                }}
              >
                {/* RB §5.3.4.1: the constellation dropped this point. The seat has
                    reached this stage and will not be told, which is a different
                    fact from a stage that has not arrived — and the only reason
                    for a row to exist with nothing in it. */}
                {row.value ?? '—'}
              </span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
